import { DataTable } from "@/components/admin/data-table";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { SortButton } from "@/components/admin/sort-button";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDistance } from "date-fns";
import { FileText, Phone, UserPlus } from "lucide-react";
import {
    RecordContextProvider,
    ShowBase,
    useListContext,
    useRecordContext,
    useShowContext,
} from "ra-core";
import {
    Link,
    Link as RouterLink,
    useLocation,
    useMatch,
    useNavigate,
} from "react-router-dom";

import { useIsMobile } from "@/hooks/use-mobile";
import { ActivityTimeline } from "../activities/ActivityTimeline";
import { Avatar } from "../contacts/Avatar";
import { TagsList } from "../contacts/TagsList";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Contact } from "../types";
import {
    AdditionalInfo,
    AddressInfo,
    CompanyAside,
    CompanyInfo,
    ContextInfo,
} from "./CompanyAside";
import { CompanyAvatar } from "./CompanyAvatar";

export const CompanyShow = () => {
  const isMobile = useIsMobile();

  return (
    <ShowBase>
      {isMobile ? <CompanyShowContentMobile /> : <CompanyShowContent />}
    </ShowBase>
  );
};

const CompanyShowContentMobile = () => {
  const { record, isPending } = useShowContext<Company>();
  if (isPending || !record) return null;

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/" />
        <div className="flex flex-1">
          <Link to="/">
            <h1 className="text-xl font-semibold">Company</h1>
          </Link>
        </div>
      </MobileHeader>

      <MobileContent>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <CompanyAvatar />
            <div className="mx-3 flex-1">
              <h2 className="text-2xl font-bold">{record.name}</h2>
            </div>
          </div>
        </div>
        <CompanyInfo record={record} />
        <AddressInfo record={record} />
        <ContextInfo record={record} />
        <AdditionalInfo record={record} />
      </MobileContent>
    </>
  );
};

const CompanyShowContent = () => {
  const { record, isPending } = useShowContext<Company>();
  const navigate = useNavigate();

  // Get tab from URL or default to "activity"
  const tabMatch = useMatch("/companies/:id/show/:tab");
  const currentTab = tabMatch?.params?.tab || "activity";

  const handleTabChange = (value: string) => {
    if (value === currentTab) return;
    if (value === "activity") {
      navigate(`/companies/${record?.id}/show`);
      return;
    }
    navigate(`/companies/${record?.id}/show/${value}`);
  };

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1">
        <Card>
          <CardContent>
            <div className="flex mb-3">
              <CompanyAvatar />
              <h5 className="text-xl ml-2 flex-1">{record.name}</h5>
            </div>
            <Tabs defaultValue={currentTab} onValueChange={handleTabChange}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
                <TabsTrigger value="docs">Documents</TabsTrigger>
              </TabsList>
              <TabsContent value="activity" className="pt-2 h-[600px]">
                <ActivityTimeline resource="companies" recordId={record.id} />
              </TabsContent>
              <TabsContent value="contacts">
                {record.nb_contacts ? (
                  <ReferenceManyField
                    reference="contacts_summary"
                    target="company_id"
                    sort={{ field: "last_name", order: "ASC" }}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-row justify-end space-x-2 mt-1">
                        {!!record.nb_contacts && (
                          <SortButton
                            fields={["last_name", "first_name", "last_seen"]}
                          />
                        )}
                        <CreateRelatedContactButton />
                      </div>
                      <ContactsIterator />
                    </div>
                  </ReferenceManyField>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row justify-end space-x-2 mt-1">
                      <CreateRelatedContactButton />
                    </div>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="docs" className="space-y-6 pt-4">
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 px-1">
                    <FileText className="w-4 h-4 text-primary" /> Quotations
                  </h4>
                  <ReferenceManyField reference="quotations" target="company_id">
                    <DataTable>
                      <DataTable.Col source="reference" label="Ref" />
                      <DataTable.Col source="status" label="Status" />
                      <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} />
                      <DataTable.Col source="date" label="Date" />
                    </DataTable>
                  </ReferenceManyField>
                </div>
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 px-1">
                    <FileText className="w-4 h-4 text-primary" /> Proforma Invoices
                  </h4>
                  <ReferenceManyField reference="proforma_invoices" target="company_id">
                    <DataTable>
                      <DataTable.Col source="reference" label="Ref" />
                      <DataTable.Col source="status" label="Status" />
                      <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} />
                      <DataTable.Col source="date" label="Date" />
                    </DataTable>
                  </ReferenceManyField>
                </div>
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 px-1">
                    <FileText className="w-4 h-4 text-primary" /> Invoices
                  </h4>
                  <ReferenceManyField reference="customer_invoices" target="company_id">
                    <DataTable>
                      <DataTable.Col source="reference" label="Ref" />
                      <DataTable.Col source="status" label="Status" />
                      <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} />
                      <DataTable.Col source="date" label="Date" />
                    </DataTable>
                  </ReferenceManyField>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <CompanyAside link="show" />
    </div>
  );
};

const ContactsIterator = () => {
  const location = useLocation();
  const { data: contacts, error, isPending } = useListContext<Contact>();

  if (isPending || error) return null;

  const now = Date.now();
  return (
    <div className="pt-0">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <div className="p-0 text-sm">
            <RouterLink
              to={`/contacts/${contact.id}/show`}
              state={{ from: location.pathname }}
              className="flex items-center justify-between hover:bg-muted py-2 transition-colors"
            >
              <div className="mr-4">
                <Avatar />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {`${contact.first_name} ${contact.last_name}`}
                </div>
                {contact.phone_jsonb?.[0]?.number && (
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {contact.phone_jsonb.map((p) => p.number).filter(Boolean).join(", ")}
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  {contact.title}
                  {contact.nb_tasks
                    ? ` - ${contact.nb_tasks} task${
                        contact.nb_tasks > 1 ? "s" : ""
                      }`
                    : ""}
                  &nbsp; &nbsp;
                  <TagsList />
                </div>
              </div>
              {contact.last_seen && (
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">
                    last activity {formatDistance(contact.last_seen, now)} ago{" "}
                    <Status status={contact.status} />
                  </div>
                </div>
              )}
            </RouterLink>
          </div>
        </RecordContextProvider>
      ))}
    </div>
  );
};

const CreateRelatedContactButton = () => {
  const company = useRecordContext<Company>();
  return (
    <Button variant="outline" asChild size="sm" className="h-9">
      <RouterLink
        to="/contacts/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <UserPlus className="h-4 w-4" />
        Add contact
      </RouterLink>
    </Button>
  );
};

